import { inject, Service } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, map, merge, type Observable, ReplaySubject, switchMap } from "rxjs";

import { AdaaHelper } from "../../core/utils";

@Service()
export class AppTitleService {
  private _translateService = inject(TranslateService);
  private _activatedRoute = inject(ActivatedRoute);
  private _titleService = inject(Title);

  /**
   * Translations are fetched from the backend, so on a reload the first
   * navigation can land before or after they arrive. Both events carry the
   * loaded translations, so either one means the active language is ready.
   * It listens from construction: the app loads the language right after
   * creating this service, and an event missed before the first navigation
   * would leave every title waiting for good.
   */
  private readonly _translationsLoaded$ = new ReplaySubject<void>(1);

  constructor() {
    merge(this._translateService.onLangChange, this._translateService.onFallbackLangChange).subscribe(() =>
      this._translationsLoaded$.next()
    );
  }

  public changeTitle(trigger$: Observable<unknown>): void {
    trigger$
      .pipe(
        map(this._getRoute.bind(this)),
        filter((route) => route.outlet === "primary"),
        switchMap(this._getPageTitle.bind(this)),
        switchMap((title: string) => this._translationsLoaded$.pipe(map(() => title)))
      )
      .subscribe(this._updatePageTitle.bind(this));
  }

  public setTitle(title: string, translate = false): void {
    if (!translate) {
      this._titleService.setTitle(`Adaa 3.0 - ${title}`);
      return;
    }

    const text = this._translateService.instant(title);
    this._titleService.setTitle(`Adaa 3.0 - ${text}`);
  }

  private _getRoute() {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private _updatePageTitle(title: string) {
    if (!AdaaHelper.isDefinedAndNotEmpty(title)) {
      return this._titleService.setTitle("Adaa 3.0");
    }

    this._translateService.get(title).subscribe({
      next: this.setTitle.bind(this),
    });
  }

  private _getPageTitle(route: ActivatedRoute) {
    return route.data.pipe(map((data) => data["title"] || this._titleFromUrl(route)));
  }

  private _titleFromUrl(route: ActivatedRoute): string {
    const url = new URL(window.location.href);
    url.pathname = ["/", route.snapshot.url.join("/")].join("");

    return url.pathname.split("/").reduce((acc, frag) => {
      if (acc && frag) {
        acc += " - ";
      }
      return acc + this._ucFirst(frag);
    });
  }

  private _ucFirst(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
